import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { sanitizeSupabaseError } from '@/lib/errorHandler';
import {
  listarConversasConsumer,
  listarMensagensConversa,
  enviarMensagemApp,
  marcarLidasConsumer,
  uploadChatAppMedia,
  type EnviarMensagemAppInput,
} from '@/services/chat-app.service';

export function useConversasConsumer() {
  return useQuery({ queryKey: ['conversas-consumer'], queryFn: listarConversasConsumer });
}

export function useChatConsumer(clienteId: string | undefined) {
  const qc = useQueryClient();

  const mensagens = useQuery({
    queryKey: ['chat-consumer', clienteId],
    queryFn: () => listarMensagensConversa(clienteId as string),
    enabled: !!clienteId,
  });

  useEffect(() => {
    if (!clienteId) return;
    const channel = supabase
      .channel(`chat-app-${clienteId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'mensagens',
          filter: `cliente_potencial_id=eq.${clienteId}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: ['chat-consumer', clienteId] });
          qc.invalidateQueries({ queryKey: ['conversas-consumer'] });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [clienteId, qc]);

  const enviar = useMutation({
    mutationFn: (input: EnviarMensagemAppInput) => enviarMensagemApp(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chat-consumer', clienteId] }),
    onError: (e) => toast.error(sanitizeSupabaseError(e)),
  });

  const enviarComMedia = useMutation({
    mutationFn: async (params: {
      file: File;
      tipoMedia: 'image' | 'audio';
      conteudo?: string;
    }) => {
      const path = await uploadChatAppMedia(clienteId as string, params.file);
      return enviarMensagemApp({
        clienteId: clienteId as string,
        conteudo: params.conteudo ?? '',
        tipoMedia: params.tipoMedia,
        mediaStoragePath: path,
        mediaMimeType: params.file.type,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chat-consumer', clienteId] }),
    onError: (e) => toast.error(sanitizeSupabaseError(e)),
  });

  const marcarLidas = useMutation({
    mutationFn: () => marcarLidasConsumer(clienteId as string),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversas-consumer'] });
      qc.invalidateQueries({ queryKey: ['conversas-consumer-badge'] });
    },
  });

  return { mensagens, enviar, enviarComMedia, marcarLidas };
}
